import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link, Navigate } from 'react-router-dom'

import Context from '#context'
import checkToast from '#toast'

import apiFetch, { defaultHeader } from '#helper/Fetch API/request'

import Input from '#comp/Custom/Input'
import Logo from '#comp/Wrapper/Logo'

import { Button, Spinner } from 'flowbite-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faCheck,
	faCircle,
	faEye,
	faEyeSlash,
} from '@fortawesome/free-solid-svg-icons'

import Footer from '../Structure/Footer'

// Same rule as the backend's former reset page (client-smartsignal).
export const MIN_PASSWORD_LENGTH = 10

/**
 * @class
 * @classdesc - Lets a user set a new password from the link in the reset email.
 * The link carries a one-time `approvalid` code, which is exchanged for an
 * access token that authorises the `setPassword` request.
 * @example
 * <Wrap routeEl={ResetPassword} />
 */
export default class ResetPassword extends Component {
	static contextType = Context

	static propTypes = {
		locations: PropTypes.shape({ search: PropTypes.string }),
	}
	static defaultProps = {
		locations: { search: '' },
	}

	state = {
		password: '',
		showPassword: false,
		submitted: false,
		loading: false,
		done: false,
	}

	code = new URLSearchParams(this.props.locations.search).get('approvalid')

	handleChangeInput = (e) => {
		this.setState({ password: e.target.value })
	}

	toggleShowPassword = () => {
		this.setState((prev) => ({ showPassword: !prev.showPassword }))
	}

	/**
	 * Exchanges the email code for a token and sets the new password.
	 * Uses the plain fetch helper so a 401 for an expired code does not
	 * trigger the logout flow of the instance router.
	 * @async
	 * @param {Event} e
	 * @returns {void}
	 */
	handleSubmit = async (e) => {
		e.preventDefault()

		const { t, instance } = this.context
		const { password, loading } = this.state

		if (loading) return
		this.setState({ submitted: true })
		if (password.length < MIN_PASSWORD_LENGTH) return

		this.setState({ loading: true })

		const tokenRequest = await apiFetch(
			`${
				instance.api
			}/Authentication/Token?grant_type=authorization_code&code=${encodeURIComponent(
				this.code
			)}`,
			{ method: 'POST' }
		)
		const accessToken = tokenRequest.ok && tokenRequest.data?.access_token

		if (!accessToken) {
			this.setState({ loading: false })
			checkToast(t, tokenRequest.status ? 11010 : 10005)
			return
		}

		const setRequest = await apiFetch(
			`${instance.api}/Authentication/setPassword`,
			defaultHeader(`Bearer ${accessToken}`, 'POST', {
				newPassword: password,
			})
		)

		if (setRequest.ok) {
			checkToast(t, 11103)
			this.setState({ done: true })
		} else {
			this.setState({ loading: false })
			checkToast(t, setRequest.status ? 11011 : 10005)
		}
	}

	renderInvalidLink() {
		const { t, instance } = this.context

		return (
			<div className="flex flex-col gap-4 text-center">
				<p>{t('login.reset.invalidLink')}</p>
				<Link to={`/${instance.shortLink}`} className="underline">
					{t('login.reset.backToLogin')}
				</Link>
			</div>
		)
	}

	renderForm() {
		const { t, instance } = this.context
		const { password, showPassword, submitted, loading } = this.state

		const lengthOk = password.length >= MIN_PASSWORD_LENGTH
		const showError = submitted && !lengthOk

		return (
			<>
				<form
					onSubmit={this.handleSubmit}
					className="flex flex-col gap-4"
					noValidate
				>
					<div>
						<h1 className="text-xl font-semibold">
							{t('login.reset.headline')}
						</h1>
						<p className="text-sm text-gray-600 dark:text-gray-400">
							{t('login.reset.description')}
						</p>
					</div>
					<Input
						name="password"
						type={showPassword ? 'text' : 'password'}
						onChange={this.handleChangeInput}
						value={password}
						required={true}
						error={showError}
						icon={
							<button
								type="button"
								onClick={this.toggleShowPassword}
								aria-label={t(
									showPassword
										? 'login.reset.hidePassword'
										: 'login.reset.showPassword'
								)}
							>
								<FontAwesomeIcon
									icon={showPassword ? faEyeSlash : faEye}
								/>
							</button>
						}
					>
						{t('login.reset.newPassword')}
					</Input>
					<p
						className={
							'flex items-center gap-2 text-sm ' +
							(lengthOk
								? 'text-green-600'
								: showError
								? 'text-red-600'
								: 'text-gray-600 dark:text-gray-400')
						}
						aria-live="polite"
					>
						<FontAwesomeIcon
							icon={lengthOk ? faCheck : faCircle}
							className={lengthOk ? '' : 'h-2 w-2'}
						/>
						{t('login.reset.minLength', {
							count: MIN_PASSWORD_LENGTH,
						})}
					</p>
					<Button type="submit" disabled={loading}>
						{loading && <Spinner size="sm" className="mr-2" />}
						{loading
							? t('login.reset.saving')
							: t('login.reset.submit')}
					</Button>
				</form>
				<Link
					to={`/${instance.shortLink}`}
					className="text-center underline"
				>
					{t('login.reset.backToLogin')}
				</Link>
			</>
		)
	}

	render() {
		const { instance } = this.context

		if (this.state.done) {
			return <Navigate to={`/${instance.shortLink}`} replace />
		}

		return (
			<>
				<div className="flex flex-col gap-8">
					<div className="w-auto h-24">
						<Logo
							instance={instance}
							className="max-h-full mx-auto"
						/>
					</div>
					{this.code ? this.renderForm() : this.renderInvalidLink()}
				</div>
				<div className="pb-1 mt-4">
					<Footer />
				</div>
			</>
		)
	}
}
